import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Heading,
  Item,
  List,
  StyledEmpty,
  TabPanel as TabPanelContainer,
} from '@gen2/app/components/base-popup-with-tabs/base-popup-with-tabs.styled';
import { Checkbox } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { TFilterInviteIndicator } from './filter';
import { ChipItem, ChipList, Info, Name } from './filter.styled';

type TItem = TFilterInviteIndicator;

const inviteIndicatorLabels: { [key: string]: string } = {
  sent: 'Sent',
  no_progress: 'No Progress',
  progressing: 'Progressing',
  awaiting_review: 'Awaiting Review',
  in_review: 'In Review',
  complete: 'Completed',
  due_today: 'Due Today',
  overdue: 'Overdue',
};

export type TIndicatorTabPanelProps<T> = {
  data: T[] | undefined;
  selectedItems: TFilterInviteIndicator[];
  setSelectedItems: (items: TFilterInviteIndicator[]) => void;
  title: string;
  subTitle?: string;
};

const IndicatorTabPanel = <T extends TItem>({
  data,
  selectedItems,
  setSelectedItems,
  title,
  subTitle = 'View invites which have a certain status',
}: TIndicatorTabPanelProps<T>) => {
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: T,
  ) => {
    if (event.target.checked) {
      setSelectedItems([...selectedItems, item]);
    } else {
      setSelectedItems(selectedItems.filter((i) => i !== item));
    }
  };

  const handleDeleteChip = (item: TFilterInviteIndicator) => {
    setSelectedItems(selectedItems.filter((i) => i !== item));
  };

  const selectedItemIds = useMemo(() => {
    return selectedItems;
  }, [selectedItems]);

  const getName = useCallback((item: TFilterInviteIndicator) => {
    return inviteIndicatorLabels[item] || item;
  }, []);

  return (
    <TabPanelContainer>
      <Heading>
        <p data-cy="title">{title}</p>
        <span>{subTitle}</span>
      </Heading>
      {data?.length ? (
        <>
          <ChipList data-cy="chip-list">
            {selectedItems.map((item) => (
              <ChipItem
                key={item}
                color="contact"
                size="small"
                label={getName(item)}
                icon={<FontAwesomeIcon icon={faTimesCircle} />}
                onClick={() => handleDeleteChip(item)}
              />
            ))}
          </ChipList>
          <List data-cy="search-result-list">
            {data.map((item) => (
              <Item key={item}>
                <Info>
                  <Name>{getName(item)}</Name>
                </Info>
                <Checkbox
                  color="secondary"
                  checked={selectedItemIds.includes(item)}
                  onChange={(event) => handleChange(event, item)}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </Item>
            ))}
          </List>
        </>
      ) : (
        <StyledEmpty>No invite statuses available.</StyledEmpty>
      )}
    </TabPanelContainer>
  );
};

export default IndicatorTabPanel;
